import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import type { RespostaRegistrada, TentativaAberta } from "../../types"
import { cronometro, letra } from "../../lib/format"
import { useCronometro } from "../../hooks/useCronometro"
import { Barra, Botao, Carregando, Erro } from "../ui"
import {
  OPCOES_BARREIRA,
  OPCOES_MOMENTO,
  OPCOES_PRAZO,
  PERGUNTA_BARREIRA,
  PERGUNTA_MOMENTO,
  PERGUNTA_PRAZO,
  SEM_RESPOSTA,
  corpoDoMomento,
  type RespostaDeMomento,
} from "../../lib/momento"

type EstadoSalvamento = "ocioso" | "salvando" | "salvo" | "erro"

/** Questões mais, quando houver, a última página com as perguntas sobre a prova. */
function paginas(t: TentativaAberta): number {
  return t.questoes.length + (t.perguntar_momento ? 1 : 0)
}

interface MapaProps {
  questoes: TentativaAberta["questoes"]
  respostas: Record<number, number | null>
  atual: number
  aoSaltar: (indice: number) => void
  variante: "grade" | "faixa"
}

/** Mapa de questões — grade na lateral do desktop, faixa rolável no mobile. */
function MapaDeQuestoes({ questoes, respostas, atual, aoSaltar, variante }: MapaProps) {
  return (
    <div
      className={variante === "grade" ? "grid grid-cols-5 gap-2" : "flex gap-2 overflow-x-auto pb-1"}
      role="listbox"
      aria-label="Ir para questão"
    >
      {questoes.map((q, indice) => {
        const respondida = (respostas[q.posicao] ?? null) !== null
        const ehAtual = indice === atual
        return (
          <button
            key={q.posicao}
            type="button"
            onClick={() => aoSaltar(indice)}
            aria-current={ehAtual ? "true" : undefined}
            aria-label={`Questão ${indice + 1}${respondida ? ", respondida" : ", sem resposta"}`}
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold tabular-nums transition ${
              respondida ? "bg-navy-900 text-white hover:bg-navy-800" : "bg-navy-50 text-navy-700 hover:bg-navy-100"
            } ${ehAtual ? "outline outline-2 outline-offset-2 outline-gold-500" : ""}`}
          >
            {indice + 1}
          </button>
        )
      })}
    </div>
  )
}

/** Uma alternativa do simulado: letra num círculo, texto ao lado, dourado quando marcada. */
function Alternativa({
  indice,
  texto,
  marcada,
  aoMarcar,
}: {
  indice: number
  texto: string
  marcada: boolean
  aoMarcar: () => void
}) {
  return (
    <button
      type="button"
      onClick={aoMarcar}
      aria-pressed={marcada}
      className={`flex w-full items-start gap-3 rounded-xl border p-4 text-left text-sm transition ${
        marcada ? "border-gold-500 bg-gold-100/50 text-navy-900" : "border-navy-100 bg-white text-slate-700 hover:border-navy-300"
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold ${
          marcada ? "bg-gold-500 text-navy-900" : "bg-navy-50 text-navy-700"
        }`}
      >
        {letra(indice)}
      </span>
      <span>{texto}</span>
    </button>
  )
}

/**
 * A última página do simulado: duas perguntas sobre a prova, no mesmo cartão
 * das questões — enunciado, alternativas com letra, o mesmo botão de
 * finalizar. A pessoa responde no ritmo em que já está, sem sentir que saiu
 * do simulado e entrou numa pesquisa. Nada é obrigatório: "Finalizar" com
 * tudo em branco é pular. A segunda pergunta depende da primeira e só aparece
 * depois dela — quem marcou diz para quando; quem não marcou diz o que falta.
 * Ver lib/momento e Usuario::MomentoDaProva.
 */
function UltimaPagina({ resposta, aoMudar }: { resposta: RespostaDeMomento; aoMudar: (r: RespostaDeMomento) => void }) {
  const momento = resposta.momento_da_prova
  const segunda =
    momento === null
      ? null
      : momento === "marcou"
        ? { pergunta: PERGUNTA_PRAZO, opcoes: OPCOES_PRAZO, campo: "prazo_da_prova" as const }
        : { pergunta: PERGUNTA_BARREIRA, opcoes: OPCOES_BARREIRA, campo: "barreira_para_marcar" as const }

  return (
    <>
      <p className="text-base leading-relaxed text-navy-900">{PERGUNTA_MOMENTO}</p>
      <div className="space-y-2" role="group" aria-label={PERGUNTA_MOMENTO}>
        {OPCOES_MOMENTO.map(([valor, texto], i) => (
          <Alternativa
            key={valor}
            indice={i}
            texto={texto}
            marcada={momento === valor}
            aoMarcar={() => aoMudar({ ...SEM_RESPOSTA, momento_da_prova: valor })}
          />
        ))}
      </div>
      {segunda && (
        <>
          <p className="pt-2 text-base leading-relaxed text-navy-900">{segunda.pergunta}</p>
          <div className="space-y-2" role="group" aria-label={segunda.pergunta}>
            {segunda.opcoes.map(([valor, texto], i) => (
              <Alternativa
                key={valor}
                indice={i}
                texto={texto}
                marcada={resposta[segunda.campo] === valor}
                aoMarcar={() => aoMudar({ ...resposta, [segunda.campo]: valor })}
              />
            ))}
          </div>
        </>
      )}
      <p className="text-xs text-slate-500">Usamos isso só para adaptar seu resultado. Pode pular.</p>
    </>
  )
}

export default function Simulado({ recarregar }: { recarregar: () => Promise<void> }) {
  const { id } = useParams<{ id: string }>()
  const navegar = useNavigate()
  const [tentativa, setTentativa] = useState<TentativaAberta | null>(null)
  const [respostas, setRespostas] = useState<Record<number, number | null>>({})
  const [indice, setIndice] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [finalizando, setFinalizando] = useState(false)
  const [salvamento, setSalvamento] = useState<EstadoSalvamento>("ocioso")
  // Padrão ligado: responder e já cair na próxima é o ritmo de quem treina.
  const [autoAvancar, setAutoAvancar] = useState(true)
  // As respostas da última página; vão junto com o finalizar.
  const [momento, setMomento] = useState<RespostaDeMomento>(SEM_RESPOSTA)
  const momentoRef = useRef(momento)
  momentoRef.current = momento

  const finalizandoRef = useRef(false)
  // Duas respostas em sequência podem voltar fora de ordem; só a última manda.
  const sequenciaRef = useRef(0)

  const { restante, sincronizar } = useCronometro(
    tentativa?.cronometrada ? tentativa.segundos_restantes : null,
    () => void finalizar()
  )

  useEffect(() => {
    api
      .get<TentativaAberta>(`/tentativas/${id}`)
      .then((t) => {
        setTentativa(t)
        setRespostas(Object.fromEntries(t.questoes.map((q) => [q.posicao, q.resposta_dada])))
        // Retoma onde parou em vez de jogar o candidato na questão 1.
        const primeiraEmBranco = t.questoes.findIndex((q) => q.resposta_dada === null)
        setIndice(primeiraEmBranco === -1 ? 0 : primeiraEmBranco)
      })
      .catch(() => navegar(`/simulado/${id}/resultado`))
  }, [id, navegar])

  const respondidas = useMemo(
    () => Object.values(respostas).filter((r) => r !== null && r !== undefined).length,
    [respostas]
  )

  async function responder(posicao: number, resposta: number) {
    if (!tentativa || finalizandoRef.current) return
    setRespostas((r) => ({ ...r, [posicao]: resposta }))
    if (autoAvancar) setIndice((i) => Math.min(paginas(tentativa) - 1, i + 1))

    setSalvamento("salvando")
    const sequencia = ++sequenciaRef.current
    try {
      const registrada = await api.post<RespostaRegistrada>(`/tentativas/${id}/responder`, { posicao, resposta })
      if (sequencia === sequenciaRef.current) {
        setSalvamento("salvo")
        if (tentativa.cronometrada) sincronizar(registrada.segundos_restantes)
      }
    } catch (e) {
      // O servidor fechou a tentativa (prazo estourou): o resultado é a verdade.
      if (e instanceof ApiError && e.status === 409) {
        recarregar()
        navegar(`/simulado/${id}/resultado`)
        return
      }
      if (sequencia === sequenciaRef.current) setSalvamento("erro")
    }
  }

  async function finalizar() {
    if (finalizandoRef.current) return
    finalizandoRef.current = true
    setFinalizando(true)
    try {
      // O momento vai pela ref: o cronômetro chama daqui de dentro de um closure velho.
      await api.post(`/tentativas/${id}/finalizar`, corpoDoMomento(momentoRef.current))
      // Sem esperar: o painel deixa de ter "simulado em andamento" e a
      // navegação para o resultado não fica presa nessa segunda chamada.
      recarregar()
      navegar(`/simulado/${id}/resultado`)
    } catch {
      setErro("Não foi possível finalizar. Tente de novo.")
      finalizandoRef.current = false
      setFinalizando(false)
    }
  }

  if (!tentativa) return <Carregando texto="Montando seu simulado…" />

  // Com as perguntas da última página, há uma página a mais que questões.
  const ultima = indice === paginas(tentativa) - 1
  const naUltimaPagina = tentativa.perguntar_momento && indice === tentativa.questoes.length
  const questao = tentativa.questoes[Math.min(indice, tentativa.questoes.length - 1)]
  const posicaoNoCabecalho = naUltimaPagina ? "Última página" : `Questão ${indice + 1} de ${tentativa.total}`
  const segundos = restante ?? tentativa.segundos_restantes
  const mostrarRelogio = tentativa.cronometrada
  const corDoRelogio = segundos < 60 ? "text-red-600" : segundos < 300 ? "text-amber-600" : "text-navy-900"

  // Cores próprias do header escuro: as do desktop (navy sobre branco) somem aqui.
  const corNoHeader = segundos < 60 ? "text-red-300" : segundos < 300 ? "text-amber-300" : "text-gold-500"
  const relogio = mostrarRelogio && (
    <span
      role="timer"
      aria-label={`Tempo restante: ${cronometro(segundos)}`}
      className={`font-mono text-xl font-bold tabular-nums ${corNoHeader} ${segundos < 60 ? "animate-pulse" : ""}`}
    >
      {cronometro(segundos)}
    </span>
  )

  const indicadorSalvamento = (
    <span className="text-xs text-navy-400" aria-live="polite">
      {salvamento === "salvando" && "salvando…"}
      {salvamento === "salvo" && "salvo ✓"}
      {salvamento === "erro" && <span className="font-medium text-red-600">erro ao salvar — toque de novo</span>}
    </span>
  )

  const avisoDeTempo = mostrarRelogio && segundos < 300 && (
    <p
      className={`rounded-xl border px-4 py-3 text-center text-sm font-medium ${
        segundos < 60 ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"
      }`}
    >
      {segundos < 60
        ? "Menos de 1 minuto! O simulado será finalizado e corrigido automaticamente."
        : "Menos de 5 minutos restantes."}
    </p>
  )

  return (
    <div className="min-h-[100dvh] bg-areia pb-28 lg:pb-10">
      {/* Cabeçalho fixo — no desktop o relógio e o mapa moram na lateral. */}
      <header className="sticky top-0 z-20 border-b border-navy-100 bg-navy-900 px-4 py-3">
        <div className="mx-auto max-w-5xl space-y-2">
          <div className="flex items-center justify-between gap-3 text-sm text-navy-100">
            <span className="truncate font-semibold text-white">{tentativa.prova}</span>
            <div className="flex items-center gap-3 lg:hidden">{relogio}</div>
            <p className="hidden text-xs text-navy-100 lg:block">
              {posicaoNoCabecalho} · {respondidas} respondidas
            </p>
          </div>
          <div className="lg:hidden">
            <Barra atual={respondidas} total={tentativa.total} />
            <p className="mt-1.5 text-xs text-navy-100">
              {posicaoNoCabecalho} · {respondidas} respondidas
            </p>
          </div>
        </div>
        {/* Mobile: faixa de questões sempre à mão */}
        <div className="mx-auto mt-2 max-w-5xl lg:hidden">
          <MapaDeQuestoes
            questoes={tentativa.questoes}
            respostas={respostas}
            atual={indice}
            aoSaltar={setIndice}
            variante="faixa"
          />
        </div>
      </header>

      <div className="mx-auto flex max-w-5xl gap-6 px-4 py-6">
        {/* Lateral-guia (desktop) */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 space-y-4">
            <div className="rounded-2xl border border-navy-100 bg-white p-5">
              {mostrarRelogio && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-widest text-navy-400">Tempo restante</p>
                  <p
                    role="timer"
                    aria-label={`Tempo restante: ${cronometro(segundos)}`}
                    className={`font-mono text-4xl font-bold leading-none tabular-nums ${corDoRelogio} ${segundos < 60 ? "animate-pulse" : ""}`}
                  >
                    {cronometro(segundos)}
                  </p>
                </>
              )}
              <p className={`text-sm text-navy-700 ${mostrarRelogio ? "mt-3" : ""}`}>
                <span className="font-semibold tabular-nums">{respondidas}</span> de {tentativa.total} respondidas
              </p>
              <div className="mt-2">
                <Barra atual={respondidas} total={tentativa.total} />
              </div>
              <div className="mt-5">
                <MapaDeQuestoes
                  questoes={tentativa.questoes}
                  respostas={respostas}
                  atual={indice}
                  aoSaltar={setIndice}
                  variante="grade"
                />
              </div>
              <div className="mt-5 flex flex-col gap-1.5 text-[11px] text-navy-400">
                <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-navy-900" aria-hidden="true" /> respondida</span>
                <span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded bg-navy-50 ring-1 ring-navy-200" aria-hidden="true" /> em branco</span>
              </div>
            </div>
            <Botao variante="secundario" className="w-full" onClick={finalizar} disabled={finalizando}>
              {finalizando ? "Corrigindo…" : "Finalizar simulado"}
            </Botao>
          </div>
        </aside>

        {/* Questão */}
        <main className="min-w-0 flex-1 space-y-4">
          {erro && <Erro mensagem={erro} />}
          {avisoDeTempo}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="inline-block rounded-full bg-navy-50 px-2.5 py-1 text-xs font-semibold text-navy-700">
              {naUltimaPagina ? "Antes do resultado" : `${questao.unidade.codigo} · ${questao.unidade.nome}`}
            </span>
            {/* Na última página não há resposta a salvar nem questão para avançar. */}
            {!naUltimaPagina && (
            <div className="flex items-center gap-3">
              {indicadorSalvamento}
              <button
                type="button"
                onClick={() => setAutoAvancar((v) => !v)}
                aria-pressed={autoAvancar}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                  autoAvancar
                    ? "border-gold-500 bg-gold-100/50 text-navy-900"
                    : "border-navy-100 bg-white text-navy-500 hover:bg-navy-50"
                }`}
              >
                Avançar ao responder
              </button>
            </div>
            )}
          </div>

          {naUltimaPagina ? (
            <UltimaPagina resposta={momento} aoMudar={setMomento} />
          ) : (
            <>
              {questao.texto_referencia && (
              <figure className="space-y-2 rounded-2xl border border-navy-100 bg-white p-4">
                <figcaption className="text-sm font-semibold text-navy-900">{questao.texto_referencia.titulo}</figcaption>
                {questao.texto_referencia.imagem_url && (
                  <img
                    src={questao.texto_referencia.imagem_url}
                    alt={questao.texto_referencia.titulo}
                    className="mx-auto max-h-72 w-auto"
                  />
                )}
                {questao.texto_referencia.corpo && (
                  <p className="whitespace-pre-line text-sm text-slate-700">{questao.texto_referencia.corpo}</p>
                )}
              </figure>
            )}

            <p className="text-base leading-relaxed text-navy-900">{questao.enunciado}</p>

              <div className="space-y-2" role="group" aria-label="Alternativas">
                {questao.alternativas.map((alternativa, i) => (
                  <Alternativa
                    key={i}
                    indice={i}
                    texto={alternativa}
                    marcada={respostas[questao.posicao] === i}
                    aoMarcar={() => responder(questao.posicao, i)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Desktop: navegação junto da questão; mobile usa a barra fixa. */}
          <div className="hidden items-center justify-between border-t border-navy-100 pt-4 lg:flex">
            <Botao variante="fantasma" onClick={() => setIndice((i) => Math.max(0, i - 1))} disabled={indice === 0}>
              Anterior
            </Botao>
            {ultima ? (
              <Botao variante="secundario" onClick={finalizar} disabled={finalizando}>
                {finalizando ? "Corrigindo…" : "Finalizar simulado"}
              </Botao>
            ) : (
              <Botao onClick={() => setIndice((i) => Math.min(paginas(tentativa) - 1, i + 1))}>Próxima</Botao>
            )}
          </div>
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 border-t border-navy-100 bg-white px-4 py-3 lg:hidden">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
          <Botao variante="fantasma" onClick={() => setIndice((i) => Math.max(0, i - 1))} disabled={indice === 0}>
            Anterior
          </Botao>
          {ultima ? (
            <Botao variante="secundario" onClick={finalizar} disabled={finalizando}>
              {finalizando ? "Corrigindo…" : "Finalizar simulado"}
            </Botao>
          ) : (
            <Botao onClick={() => setIndice((i) => Math.min(paginas(tentativa) - 1, i + 1))}>Próxima</Botao>
          )}
        </div>
      </nav>
    </div>
  )
}
